import React, { useEffect, useMemo, useState } from "react";
import StarIcon from "@mui/icons-material/Star";
import { teal } from "@mui/material/colors";
import {
  Alert,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import "./styles.css";
import {
  Add,
  AddShoppingCart,
  FavoriteBorder,
  LocalShipping,
  Remove,
  Shield,
  Wallet,
  WorkspacePremium,
} from "@mui/icons-material";
import { SimilarProduct } from "./SimilarProduct";

import { ReviewCard } from "../Review/ReviewCard";
import { useAppDispatch } from "../../../State/Store";
import { useAppSelector } from "./../../../State/Store";
import {
  fetchProductById,
  fetchSimilarProducts,
  setCurrentProduct,
} from "../../../State/customer/ProductSlice";
import { useParams } from "react-router-dom";
import {
  addCartItemToCart,
  AddItemRequest,
} from "./../../../State/customer/CartSlice";
import { fetchReviewsByProductId } from "../../../State/customer/ReviewSlice";
import { colors } from "./../../data/Filter/color";
import CheckIcon from "@mui/icons-material/Check";
import { addProductToWishlist } from "../../../State/customer/WishlistSlice";
export const ProductDetails = () => {
  const [quantity, setQuantity] = useState(1);
  const [addStatus, setAddStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [addError, setAddError] = useState<string | null>(null);
  const { productId } = useParams();
  const dispatch = useAppDispatch();
  const { product, review } = useAppSelector((store) => store);
  const [activeImage, setActiveImage] = useState(0);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [lastMode, setLastMode] = useState<Mode | null>(null);
  const baseSellingPrice = product.product?.sellingPrice ?? 0;
  const baseMrpPrice = product.product?.mrpPrice ?? 0;
  
  type Mode = "cart" | "wishlist";
  // unique sizes, filtered by chosen color (so users only see valid combos)
  const sizes = useMemo(() => {
    const details = product.product?.details ?? [];
    const filtered = color ? details.filter((d) => d.color === color) : details;
    return Array.from(
      new Set(filtered.map((d) => d.size).filter(Boolean))
    ) as string[];
  }, [product.product?.details, color]);

  const normalize = (value?: string) =>
  value?.trim().toLowerCase() ?? "";

const selectedDetail = useMemo(() => {
  const details = product.product?.details ?? [];

  return details.find(
    (d) =>
      normalize(d.size) === normalize(size) &&
      normalize(d.color) === normalize(color)
  );
}, [product.product?.details, size, color]);

const selectedMrpPrice = baseMrpPrice;
const selectedSellingPrice =
  selectedDetail?.sellingPrice ??
  baseSellingPrice;
const selectedDiscountPercentage =
  selectedMrpPrice > 0
    ? Math.round(((selectedMrpPrice - selectedSellingPrice) / selectedMrpPrice) * 100)
    : 0;

const hasVariantPrice =
  selectedDetail?.sellingPrice != null &&
  selectedDetail.sellingPrice !== baseSellingPrice;
  const canSubmit =
    !!product.product?.id &&
    !!size &&
    !!color &&
    (selectedDetail?.stock ?? 0) > 0 &&
    quantity > 0;
  const colorOptions = useMemo(() => {
    const details = product?.product?.details ?? [];
    const names: string[] = details
      .flatMap((d: any) => (Array.isArray(d?.color) ? d.color : [d?.color]))
      .filter(Boolean);
    return Array.from(new Set(names)); // -> string[]
  }, [product?.product?.details]);
  const getHex = (name: string) =>
    (colors as Array<{ name: string; hex: string }> | undefined)?.find(
      (c) => c.name === name
    )?.hex ?? name;
  const averageRating = review.reviews.length
    ? review.reviews.reduce((sum, review) => sum + review.rating, 0) /
      review.reviews.length
    : 0; // If no reviews, return 0
  useEffect(() => {
    dispatch(fetchProductById(Number(productId)))
      .unwrap()
      .then(() => dispatch(setCurrentProduct(Number(productId))));

    dispatch(fetchReviewsByProductId(Number(productId)));
    if (review.isDeleted) {
      dispatch(fetchReviewsByProductId(Number(productId)));
    }
  }, [productId, review.isDeleted]);
  const handleActiveImage = (value: number) => {
    setActiveImage(value);
  };
  const handleChange = (event: SelectChangeEvent) => {
    setSize(event.target.value as string);
    setQuantity(1);
  };
  
  const handleColorChange = (event: SelectChangeEvent) => {
    setColor(event.target.value as string);
    setSize("");
    setQuantity(1);
  };

  const handleAdd = async (mode: Mode) => {
    if (!product.product?.id) return;
    setLastMode(mode);  
    if (!size || !color) {
      setAddStatus("error");
      setAddError("Please select size and color.");
      return;
    }

    if (mode === "cart" && (selectedDetail?.stock ?? 0) <= 0) {
      setAddStatus("error");
      setAddError("This variant is out of stock.");
      return;
    }

    const payload: AddItemRequest = {
      productId: product.product.id,
      size,
      color,
      quantity,
    };

    try {
      setAddStatus("loading");
      setAddError(null);
      if (mode === "cart") {
        console.log("payload: ",payload);
        await dispatch(addCartItemToCart(payload)).unwrap();
      } else {
        await dispatch(addProductToWishlist(payload)).unwrap();
      }
      setAddStatus("success");
      setTimeout(() => setAddStatus("idle"), 1800);
    } catch (e: any) {
      setAddStatus("error");
      setAddError(e?.message ?? "Something went wrong");
    }
  };
  
  useEffect(() => {
    if (product.product?.categoryId) {
      dispatch(
        fetchSimilarProducts({
          categoryId: product.product.categoryId,
          pageNumber: 0,
        })
      );
    }
  }, [product.product?.categoryId]);

  return (
    <div className="px-5  pt-10 xl:w-[90%] m-auto">
      <div className="">
        {addStatus === "success"&&(
          <Alert
            className="fixed top-[5rem] right-[20vw] z-50 shadow-lg max-w-sm"
            severity="success"
          >
            Added to {lastMode}.
          </Alert>
        )}

        {/* Error */}
        {addStatus === "error" && (
          <Alert
            className="fixed top-[5rem] right-[20vw] z-50 shadow-lg max-w-sm"
            severity="error"
            onClose={() => setAddStatus("idle")}
          >
            {addError}
          </Alert>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <section className="flex flex-col lg:flex-row gap-5">
          <div className="w-full lg:w-[20%] flex flex-wrap lg:flex-col gap-3">
            {product.product?.images.map((item, index) => (
              <img
                onClick={() => {
                  handleActiveImage(index);
                }}
                key={index}
                className="lg:w-full w-[50px] cuso  rounded-md"
                src={item}
              />
            ))}
          </div>
          <div className="w-full lg:w-[85%]">
            <img
              className="w-full rounded-md"
              src={product.product?.images[activeImage]}
              alt=""
            />
          </div>
        </section>

        <section>
          <h1 className="font-bold text-3xl text-primary">
            {product.product?.sellerName}
          </h1>
          <p className="text-gray-500 font-semibold text-xl ">
            {product.product?.title}
          </p>
          {/* box display rating */}
          <div className="flex justify-between items-center py-2 border w-[180px] px-3 mt-5">
            <div className="flex gap-1 items-center ">
              <span>{averageRating}</span>
              <StarIcon sx={{ color: teal[500], fontSize: "17px" }} />
            </div>
            <Divider orientation="vertical" flexItem />
            <span>123 Rating</span>
          </div>
          {/* box display price */}
          <div className="">
  <div className="price flex items-center gap-3 mt-5 text-2xl">
    <span className="font-sans text-gray-800">
      {selectedSellingPrice}€
    </span>

    {selectedMrpPrice > selectedSellingPrice && (
      <span className="line-through text-gray-400">
        {selectedMrpPrice}€
      </span>
    )}

    {selectedDiscountPercentage > 0 && (
      <span className="text-primary font-semibold">
        {selectedDiscountPercentage}%
      </span>
    )}
  </div>

  {hasVariantPrice && (
    <p className="text-sm text-gray-500 mt-1">
      Price updated for selected {selectedDetail?.size} / {selectedDetail?.color}
    </p>
  )}

  {!size || !color ? (
    <p className="text-sm text-gray-500 mt-1">
      Select size and color to see exact variant price.
    </p>
  ) : null}

  <p className="text-md">
    Inclusive of all taxes. Free Shipping above 40€.
  </p>
</div>
          {/* Authenticate quality assurance etc */}
          <div className="mt-7 space-y-3 ">
            <div className="flex items-center gap-4">
              <Shield sx={{ color: teal[500] }} />
              <p>Authentic & Quality Assured</p>
            </div>
            <div className="flex items-center gap-4">
              <WorkspacePremium sx={{ color: teal[500] }} />
              <p>100% money back guarantee</p>
            </div>
            <div className="flex items-center gap-4">
              <LocalShipping sx={{ color: teal[500] }} />
              <p>Free Shipping and Returns</p>
            </div>
            <div className="flex items-center gap-4">
              <Wallet sx={{ color: teal[500] }} />
              <p>Pay on delivery might be available</p>
            </div>
          </div>
          {/* Description */}
          {/* collapses extra spaces but preserves newlines */}
          <p className="mt-5 whitespace-pre-line">
            {product.product?.description}
          </p>
          {/* Quantity box */}

          <div className="">
            <div className="flex justify-center md:justify-start items-center mt-7 space-y-2 space-x-1 md:space-x-10">
              {/* <div className="mt-7 space-y-2"> */}
              <div className="">
                <h1>QUANTITY</h1>
                <div className="flex items-center gap-2  md:w-[140px] justify-between">
                  <Button
                    disabled={quantity == 1}
                    onClick={() => setQuantity(quantity - 1)}
                  >
                    <Remove />
                  </Button>
                  <span>{quantity}</span>
                  <Button disabled={quantity >= (selectedDetail?.stock ?? 0)} onClick={() => setQuantity(quantity + 1)}>
                    <Add />
                  </Button>
                </div>
              </div>
              {/* </div> */}
              <div className="">
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="demo-simple-select-label">SIZE</InputLabel>
                  {/* <div className="flex items-center gap-2 w-[140px] justify-between"> */}
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={size}
                    label="size"
                    // defaultValue="S"
                    onChange={handleChange}
                    className="w-[5rem] md:w-[7rem]"
                  >
                    {/* {product.product?.details.map((d,index)=>(
                  <MenuItem key={index}>{d.size}</MenuItem>
                ))} */}

                    {sizes?.map((s: string) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}

                    {/* {(product.product?.sizes ?? []).map(s => (
    <MenuItem key={s} value={s}>{s}</MenuItem>
    ))} */}
                  </Select>
                  {/* </div> */}
                </FormControl>
              </div>
              <div className="">
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="demo-simple-select-label">COLORS</InputLabel>
                  {/* <div className="flex items-center gap-2 w-[140px] justify-between"> */}
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={color}
                    label="color"
                    // defaultValue="S"
                    onChange={handleColorChange}
                    className="w-[5rem] md:w-[7rem]"
                  >
                    {colorOptions.map((name) => (
                      <MenuItem key={name} value={name}>
                        <span
                          style={{
                            display: "inline-block",
                            width: 14,
                            height: 14,
                            borderRadius: "50%",
                            marginRight: 8,
                            border: "1px solid rgba(0,0,0,0.2)",
                            background: getHex(name),
                          }}
                        />
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                  {/* </div> */}
                </FormControl>
              </div>
            </div>
          </div>
          {/* 2 Button for add to cart and wish list */}
          <div className="mt-12 flex items-center gap-5">
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddShoppingCart />}
              disabled={!canSubmit || addStatus === "loading"}
              sx={{ py: "1rem", cursor: "pointer" }}
              onClick={() => handleAdd("cart")}
            >
              Add to Cart
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FavoriteBorder />}
              sx={{ py: "1rem" }}
              onClick={() => handleAdd("wishlist")}
            >
              Add to Wish
            </Button>
          </div>

          {/* Review */}
          {review.reviews &&
            review.reviews.map((item) => (
              <div className="mt-12 space-y-7">
                <Divider textAlign="right" flexItem />
                <ReviewCard review={item} />
                <Divider textAlign="right" flexItem />
              </div>
            ))}
        </section>
      </div>

      {/* similar product  */}
      {product.similarProduct.length > 0 && (
        <div className="mt-20 ">
          <h1 className="text-lg font-bold text-center lg:text-3xl ">
            Similar Product
          </h1>
          <div className="pt-5  lg:space-x-5">
            <SimilarProduct similarProduct={product.similarProduct} />
          </div>
        </div>
      )}
    </div>
  );
};
